import { readPhoto, readPhotos, writePhoto } from './photo-storage.js';
/**
 * Open and access the IndexedDB. One database per origin, lazily created on
 * first call. All writes are autosaved — caller never has to "save" anything,
 * just call the update helpers.
 */

import { openDB, type IDBPDatabase } from 'idb';
import type {
  BatteryChecklistKind,
  FieldReport,
  PhotoQuarterTurn,
  PhotoRotationDegrees,
} from '@cer-autofill/shared';
import { rotatePreparedPhoto } from '../lib/image.js';
import { DB_NAME, DB_VERSION, type FieldDbSchema } from './schema.js';

let dbPromise: Promise<IDBPDatabase<FieldDbSchema>> | null = null;

export function getDb(): Promise<IDBPDatabase<FieldDbSchema>> {
  if (!dbPromise) {
    dbPromise = openDB<FieldDbSchema>(DB_NAME, DB_VERSION, {
      upgrade(db, oldVersion) {
        if (oldVersion < 1) {
          db.createObjectStore('session', { keyPath: 'id' });
          db.createObjectStore('jobs', { keyPath: 'jobId' });
          const submissions = db.createObjectStore('submissions', { keyPath: 'jobId' });
          submissions.createIndex('by-status', 'status');
          const photos = db.createObjectStore('photos', { keyPath: 'id' });
          photos.createIndex('by-job', 'jobId');
          photos.createIndex('by-status', 'uploadStatus');
          db.createObjectStore('syncQueue', { keyPath: 'id' });
          db.createObjectStore('clauseLibrary', { keyPath: 'id' });
        }
      },
    });
  }
  return dbPromise;
}

/* ────────────────────────────────────────────────────────────────────────────
 * Session
 * ──────────────────────────────────────────────────────────────────────────── */

export async function getSession() {
  const db = await getDb();
  return db.get('session', 'current');
}

export async function setSession(value: {
  deviceToken: string;
  deviceId: string;
  inspectorId: string;
}) {
  const db = await getDb();
  await db.put('session', {
    id: 'current',
    ...value,
    source: 'legacy',
    pairedAt: new Date().toISOString(),
  });
}

export async function setCognitoSession(value: {
  displayName: string;
  email: string;
  deviceId: string;
  inspectorId?: string;
  idToken: string;
  accessToken?: string;
  refreshToken?: string;
  expiresInSeconds?: number;
}) {
  const db = await getDb();
  const expiresAt = Date.now() + (value.expiresInSeconds ?? 3600) * 1000;
  await db.put('session', {
    id: 'current',
    source: 'cognito',
    displayName: value.displayName,
    email: value.email,
    deviceId: value.deviceId,
    inspectorId: value.inspectorId ?? value.displayName,
    idToken: value.idToken,
    ...(value.accessToken !== undefined ? { accessToken: value.accessToken } : {}),
    ...(value.refreshToken !== undefined ? { refreshToken: value.refreshToken } : {}),
    expiresAt,
    pairedAt: new Date().toISOString(),
  });
}

export async function clearSession() {
  const db = await getDb();
  const tx = db.transaction(['session', 'jobs'], 'readwrite');
  await Promise.all([tx.objectStore('session').delete('current'), tx.objectStore('jobs').clear()]);
  await tx.done;
}

export async function updateCognitoTokens(value: {
  idToken: string;
  accessToken?: string;
  expiresInSeconds?: number;
  refreshToken?: string;
}) {
  const db = await getDb();
  const session = await db.get('session', 'current');
  if (!session || session.source !== 'cognito') {
    throw new Error('Cannot refresh a non-Cognito session.');
  }
  await db.put('session', {
    ...session,
    idToken: value.idToken,
    ...(value.accessToken !== undefined ? { accessToken: value.accessToken } : {}),
    ...(value.refreshToken !== undefined ? { refreshToken: value.refreshToken } : {}),
    expiresAt: Date.now() + (value.expiresInSeconds ?? 3600) * 1000,
  });
}

/* ────────────────────────────────────────────────────────────────────────────
 * Today's jobs
 * ──────────────────────────────────────────────────────────────────────────── */

export async function getTodaysJobs() {
  const db = await getDb();
  return db.getAll('jobs');
}

export async function cacheJobs(
  jobs: {
    jobId: string;
    siteAddress?: string;
    tenderer?: string;
    scheduledDate?: string;
    inspectionType: 'pv' | 'battery' | 'combo';
    batteryChecklistKind?: BatteryChecklistKind;
    comboPartnerJobId?: string;
    bookingNotes?: string;
  }[],
) {
  const db = await getDb();
  const tx = db.transaction('jobs', 'readwrite');
  const now = new Date().toISOString();
  // This is a snapshot, not an append-only cache. Keeping yesterday's rows
  // leaks stale work into the next day and across inspector switches.
  await tx.store.clear();
  await Promise.all(jobs.map((j) => tx.store.put({ ...j, cachedAt: now })));
  await tx.done;
}

export async function cacheJob(job: {
  jobId: string;
  siteAddress?: string;
  tenderer?: string;
  scheduledDate?: string;
  inspectionType: 'pv' | 'battery' | 'combo';
  batteryChecklistKind?: BatteryChecklistKind;
  comboPartnerJobId?: string;
  bookingNotes?: string;
  batteryLocationCapture?: boolean;
}) {
  const db = await getDb();
  await db.put('jobs', { ...job, cachedAt: new Date().toISOString() });
}

export async function getJob(jobId: string) {
  const db = await getDb();
  return db.get('jobs', jobId);
}

/* ────────────────────────────────────────────────────────────────────────────
 * Submissions (the inspector's working draft)
 * ──────────────────────────────────────────────────────────────────────────── */

export async function getSubmission(jobId: string) {
  const db = await getDb();
  return db.get('submissions', jobId);
}

export async function upsertSubmission(jobId: string, submission: FieldReport) {
  const db = await getDb();
  const existing = await db.get('submissions', jobId);
  const next = {
    jobId,
    updatedAt: new Date().toISOString(),
    submission,
    status: existing?.status ?? ('draft' as const),
    ...(existing?.ackedAt !== undefined ? { ackedAt: existing.ackedAt } : {}),
    ...(existing?.lastError !== undefined ? { lastError: existing.lastError } : {}),
  };
  await db.put('submissions', next);
}

export async function markSubmissionPending(jobId: string) {
  const db = await getDb();
  const row = await db.get('submissions', jobId);
  if (!row) return;
  const next = { ...row, status: 'pending' as const };
  delete next.ackedAt;
  delete next.lastError;
  await db.put('submissions', next);
}

export async function markSubmissionSent(jobId: string) {
  const db = await getDb();
  const row = await db.get('submissions', jobId);
  if (!row) return;
  await db.put('submissions', { ...row, status: 'sent', ackedAt: new Date().toISOString() });
}

export async function markSubmissionFailed(jobId: string, error: string) {
  const db = await getDb();
  const row = await db.get('submissions', jobId);
  if (!row) return;
  await db.put('submissions', { ...row, status: 'failed', lastError: error });
}

/* ────────────────────────────────────────────────────────────────────────────
 * Photos
 * ──────────────────────────────────────────────────────────────────────────── */

export async function savePhoto(photo: {
  id: string;
  jobId: string;
  promptKey: string;
  filename: string;
  mimeType: string;
  capturedAt: string;
  blob: Blob;
  importBatchId?: string;
  evidenceFooterPx?: number;
}) {
  const db = await getDb();
  await writePhoto(db, { ...photo, uploadStatus: 'pending' });
}

export async function getPhotosForJob(jobId: string) {
  const db = await getDb();
  return readPhotos(db, 'by-job', jobId);
}

/**
 * How long an uploaded photo's bytes stay on the device after the server has
 * confirmed them.
 *
 * Not zero on purpose. The server has the photo the moment the upload acks, so
 * the local copy is redundant for durability — but it is what renders the
 * thumbnails an inspector looks at while they are still on site, and same-day
 * review is the common case. A week keeps the working set and reclaims
 * everything older.
 */
const BLOB_RETENTION_DAYS = 7;

/**
 * Release the bytes of photos the server has confirmed and that are past the
 * retention window. Returns what was freed, for logging.
 *
 * Without this, blobs accumulate forever: `markPhotoUploaded` has only ever
 * flipped a status flag. That was survivable while photos arrived a handful at
 * a time, but bulk import makes it a real problem — a few days of jobs would
 * leave a gigabyte of already-uploaded bytes on the tablet, and iOS Safari
 * evicts PWA storage under pressure, taking the photos that have NOT been
 * uploaded with it. The pruner exists to protect the un-uploaded ones.
 *
 * Only ever touches rows with `uploadStatus === 'uploaded'`. A pending,
 * uploading or failed photo keeps its bytes no matter how old it is — that
 * copy may be the only one in existence.
 */
export async function prunePhotoBlobs(options?: {
  retentionDays?: number;
  now?: number;
}): Promise<{ released: number; bytesFreed: number }> {
  const retentionDays = options?.retentionDays ?? BLOB_RETENTION_DAYS;
  const cutoff = (options?.now ?? Date.now()) - retentionDays * 24 * 60 * 60 * 1000;
  const db = await getDb();
  const uploaded = await readPhotos(db, 'by-status', 'uploaded');

  let released = 0;
  let bytesFreed = 0;
  for (const row of uploaded) {
    if (!shouldReleaseBlob(row, cutoff)) continue;
    bytesFreed += row.blob?.size ?? 0;
    released += 1;
    const { blob: _blob, ...rest } = row;
    await writePhoto(db, { ...rest, blobReleasedAt: new Date().toISOString() });
  }
  return { released, bytesFreed };
}

/**
 * Whether one photo row's bytes may be released. Pure, and the whole of the
 * decision — the caller only iterates and writes.
 *
 * Every branch here is a way to lose an inspector's only copy of a photo, so
 * each is a deliberate refusal rather than an incidental one.
 */
export function shouldReleaseBlob(
  row: {
    uploadStatus: 'pending' | 'uploading' | 'uploaded' | 'failed';
    uploadedAt?: string;
    capturedAt: string;
    blob?: Blob;
  },
  cutoff: number,
): boolean {
  // Nothing to release.
  if (!row.blob) return false;
  // The server does not have it yet. Age is irrelevant — this copy may be the
  // only one that exists.
  if (row.uploadStatus !== 'uploaded') return false;
  // No uploadedAt on a row written before that field existed; fall back to
  // capture time rather than treating it as brand new and keeping it forever.
  const confirmedAt = Date.parse(row.uploadedAt ?? row.capturedAt);
  // An unparseable date is not evidence that the photo is old.
  if (Number.isNaN(confirmedAt)) return false;
  return confirmedAt <= cutoff;
}

export async function getPendingPhotos() {
  const db = await getDb();
  return readPhotos(db, 'by-status', 'pending');
}

/** The server object a later rotate has to name, so a retry is not a second turn. */
export interface ServerPhotoVersion {
  storageKey: string;
  uploadedAt: string;
}

/**
 * Confirm the upload only when the row is still the bytes that were sent.
 *
 * `uploadedAt` is when this device confirmed the upload, and the 7-day prune
 * reads it. The server's own upload time is `serverUploadedAt`. Returns false
 * when the row changed (or disappeared) so the caller can send the new bytes.
 */
export async function markPhotoUploaded(
  id: string,
  expectedContentRev: number,
  server?: ServerPhotoVersion,
): Promise<boolean> {
  const db = await getDb();
  const row = await readPhoto(db, id);
  if (!row) return false;
  if ((row.contentRev ?? 0) !== expectedContentRev) return false;
  await writePhoto(db, {
    ...row,
    uploadStatus: 'uploaded',
    uploadedAt: new Date().toISOString(),
    ...(server ? { serverStorageKey: server.storageKey, serverUploadedAt: server.uploadedAt } : {}),
  });
  return true;
}

export async function markPhotoFailed(id: string, error: string) {
  const db = await getDb();
  const row = await readPhoto(db, id);
  if (!row) return;
  await writePhoto(db, { ...row, uploadStatus: 'failed', lastError: error });
}

/* ────────────────────────────────────────────────────────────────────────────
 * Sync queue (drives the upload loop)
 * ──────────────────────────────────────────────────────────────────────────── */

export async function enqueueSubmission(jobId: string) {
  const db = await getDb();
  await db.put('syncQueue', {
    id: `submission:${jobId}`,
    kind: 'submission',
    jobId,
    enqueuedAt: new Date().toISOString(),
    attempts: 0,
  });
}

export async function enqueuePhoto(photoId: string, jobId: string) {
  const db = await getDb();
  await db.put('syncQueue', {
    id: `photo:${photoId}`,
    kind: 'photo',
    photoId,
    jobId,
    enqueuedAt: new Date().toISOString(),
    attempts: 0,
  });
}

/**
 * The photo's IndexedDB row still holds a Blob handle, but the bytes behind it
 * are gone — iOS Safari can evict a blob's backing file while keeping the
 * record (WebKit blob loss). Retrying can never succeed; the only fix is
 * re-importing the photo from the camera roll. BTY6916276/BTY7036771
 * (2026-08-14) lost 51 photos to this: every S3 PUT failed instantly and
 * every multipart fallback posted a bodyless form.
 */
export const PHOTO_DATA_LOST_ERROR =
  'Photo data was lost on this device before upload — re-import it from the camera roll';

/**
 * Re-queue photos that exhausted automatic retries but still have local bytes.
 * Resets uploadStatus to pending so submissions are not permanently blocked.
 */
export async function requeueFailedPhotos(): Promise<number> {
  const db = await getDb();
  const failed = await readPhotos(db, 'by-status', 'failed');
  let count = 0;
  for (const photo of failed) {
    if (!photo.blob) continue;
    // A dead blob keeps its handle, so the presence check above cannot catch
    // it — requeueing would retry a permanently unreadable photo forever.
    if (photo.lastError === PHOTO_DATA_LOST_ERROR) continue;
    const { lastError: _cleared, ...rest } = photo;
    await writePhoto(db, {
      ...rest,
      uploadStatus: 'pending',
    });
    await enqueuePhoto(photo.id, photo.jobId);
    count += 1;
  }
  return count;
}

/**
 * Queue a server-side re-key. Keyed by photo id, so re-filing the same photo
 * twice before a sync leaves one item carrying the latest target rather than
 * two items racing.
 */
/**
 * Add a clockwise quarter turn to a net angle. 0 means the turns cancel.
 * The rotate queue deletes its item when this returns 0.
 */
export function combineQuarterTurns(
  current: number | undefined,
  delta: PhotoQuarterTurn,
): PhotoRotationDegrees {
  const sum = ((current ?? 0) + delta) % 360;
  if (sum === 0 || sum === 90 || sum === 180 || sum === 270) return sum;
  throw new Error('Rotation must be a quarter turn.');
}

/**
 * Queue a server-side rotate. Keyed by photo id, so turning the same photo
 * again before a sync leaves one item carrying the cumulative angle. A net
 * of 0 removes the item — the server copy is already in the saved orientation.
 */
export async function enqueueRotate(
  photoId: string,
  jobId: string,
  degrees: PhotoQuarterTurn,
  base?: ServerPhotoVersion,
) {
  const db = await getDb();
  const id = `rotate:${photoId}`;
  const existing = await db.get('syncQueue', id);
  const current = existing?.kind === 'rotate' ? existing.degrees : undefined;
  const next = combineQuarterTurns(current, degrees);
  if (next === 0) {
    if (existing) await db.delete('syncQueue', id);
    return;
  }
  // Later turns collapse into one angle counted from the first server record.
  // Replacing that base with a newer one would make the cumulative angle apply
  // to a photo that already includes the earlier turn.
  const keptBase =
    existing?.kind === 'rotate'
      ? {
          ...(existing.baseStorageKey ? { baseStorageKey: existing.baseStorageKey } : {}),
          ...(existing.baseUploadedAt ? { baseUploadedAt: existing.baseUploadedAt } : {}),
        }
      : base
        ? { baseStorageKey: base.storageKey, baseUploadedAt: base.uploadedAt }
        : {};
  await db.put('syncQueue', {
    id,
    kind: 'rotate',
    photoId,
    jobId,
    degrees: next,
    enqueuedAt: existing?.kind === 'rotate' ? existing.enqueuedAt : new Date().toISOString(),
    attempts: 0,
    ...keptBase,
  });
}

/**
 * Bake a quarter turn into the local blob when this device still holds one.
 *
 * Returns whether the server already has the photo, so the caller can queue
 * a rotate instead of relying on the pending upload. A pruned blob is left
 * alone; the preview reloads from the server after the queued rotate lands.
 */
export async function rotatePhotoLocally(
  photoId: string,
  degrees: PhotoQuarterTurn,
): Promise<
  | {
      jobId: string;
      alreadyUploaded: boolean;
      blob?: Blob;
      filename: string;
      mimeType: string;
      byteSize: number;
      contentRev: number;
      server?: ServerPhotoVersion;
    }
  | undefined
> {
  const db = await getDb();
  const row = await readPhoto(db, photoId);
  if (!row) return undefined;
  const alreadyUploaded = row.uploadStatus === 'uploaded';
  const server = storedServerVersion(row);
  if (!row.blob) {
    return {
      jobId: row.jobId,
      alreadyUploaded,
      filename: row.filename,
      mimeType: row.mimeType,
      byteSize: 0,
      contentRev: row.contentRev ?? 0,
      ...(server ? { server } : {}),
    };
  }
  const rotated = await rotatePreparedPhoto(row.blob, degrees, row.evidenceFooterPx);
  const filename = jpegFileName(row.filename);
  const contentRev = (row.contentRev ?? 0) + 1;
  const rotationDegrees = combineQuarterTurns(row.rotationDegrees, degrees);
  const { rotateNotice: _notice, evidenceFooterPx: _footer, ...rest } = row;
  await writePhoto(db, {
    ...rest,
    blob: rotated.blob,
    filename,
    mimeType: 'image/jpeg',
    rotationDegrees,
    contentRev,
    ...(rotated.evidenceFooterPx !== undefined
      ? { evidenceFooterPx: rotated.evidenceFooterPx }
      : {}),
  });
  return {
    jobId: row.jobId,
    alreadyUploaded,
    blob: rotated.blob,
    filename,
    mimeType: 'image/jpeg',
    byteSize: rotated.blob.size,
    contentRev,
    ...(server ? { server } : {}),
  };
}

function storedServerVersion(row: {
  serverStorageKey?: string;
  serverUploadedAt?: string;
}): ServerPhotoVersion | undefined {
  if (!row.serverStorageKey || !row.serverUploadedAt) return undefined;
  return { storageKey: row.serverStorageKey, uploadedAt: row.serverUploadedAt };
}

function jpegFileName(filename: string): string {
  if (/\.jpe?g$/i.test(filename)) return filename;
  const slash = Math.max(filename.lastIndexOf('/'), filename.lastIndexOf('\\'));
  const dot = filename.lastIndexOf('.');
  if (dot > slash) return `${filename.slice(0, dot)}.jpg`;
  return `${filename}.jpg`;
}

/** Replace a local blob with bytes just read from the server. */
export async function replacePhotoBlob(photoId: string, blob: Blob): Promise<void> {
  const db = await getDb();
  const row = await readPhoto(db, photoId);
  if (!row) return;
  await writePhoto(db, {
    ...row,
    blob,
    mimeType: blob.type || row.mimeType || 'image/jpeg',
    contentRev: (row.contentRev ?? 0) + 1,
  });
}

/** Drop the local bytes so the next preview has to read the server copy. */
export async function releasePhotoBlob(photoId: string): Promise<void> {
  const db = await getDb();
  const row = await readPhoto(db, photoId);
  if (!row) return;
  const { blob: _blob, ...rest } = row;
  await writePhoto(db, {
    ...rest,
    blobReleasedAt: new Date().toISOString(),
    contentRev: (row.contentRev ?? 0) + 1,
  });
}

export async function setPhotoNotice(photoId: string, notice: string | undefined): Promise<void> {
  const db = await getDb();
  const row = await readPhoto(db, photoId);
  if (!row) return;
  if (!notice) {
    const { rotateNotice: _notice, ...rest } = row;
    await writePhoto(db, rest);
    return;
  }
  await writePhoto(db, { ...row, rotateNotice: notice });
}

/** A queued rotate landed. Drop the notice and remember the new server record. */
export async function notePhotoRotatedOnServer(
  photoId: string,
  server?: ServerPhotoVersion,
): Promise<void> {
  const db = await getDb();
  const row = await readPhoto(db, photoId);
  if (!row) return;
  const { rotateNotice: _notice, ...rest } = row;
  await writePhoto(db, {
    ...rest,
    contentRev: (row.contentRev ?? 0) + 1,
    ...(server ? { serverStorageKey: server.storageKey, serverUploadedAt: server.uploadedAt } : {}),
  });
}

/** Keep the server record from a 409 so the next turn is not counted from the old one. */
export async function rememberServerPhoto(
  photoId: string,
  server: ServerPhotoVersion,
): Promise<void> {
  const db = await getDb();
  const row = await readPhoto(db, photoId);
  if (!row) return;
  await writePhoto(db, {
    ...row,
    serverStorageKey: server.storageKey,
    serverUploadedAt: server.uploadedAt,
  });
}

export async function enqueueRekey(photoId: string, jobId: string, promptKey: string) {
  const db = await getDb();
  await db.put('syncQueue', {
    id: `rekey:${photoId}`,
    kind: 'rekey',
    photoId,
    jobId,
    promptKey,
    enqueuedAt: new Date().toISOString(),
    attempts: 0,
  });
}

/**
 * Re-file a photo locally, across every store that references its promptKey.
 *
 * Returns what the caller needs to decide whether a server round-trip is owed:
 * an un-uploaded photo needs none, because its pending upload carries the new
 * key already.
 */
export async function rekeyPhotoLocally(
  photoId: string,
  promptKey: string,
): Promise<
  | { jobId: string; filename: string; previousPromptKey: string; alreadyUploaded: boolean }
  | undefined
> {
  const db = await getDb();
  const row = await readPhoto(db, photoId);
  if (!row) return undefined;
  // Derive the new name here, where the original extension is in hand — a
  // re-filed PNG must not be renamed to .jpg.
  const ext = /\.([A-Za-z0-9]+)$/.exec(row.filename)?.[1] ?? 'jpg';
  const filename = `${row.jobId}_${sanitisePromptKeyForFilename(promptKey)}_${photoId.slice(0, 8)}.${ext}`;
  await writePhoto(db, { ...row, promptKey, filename });
  return {
    jobId: row.jobId,
    filename,
    previousPromptKey: row.promptKey,
    alreadyUploaded: row.uploadStatus === 'uploaded',
  };
}

/** Mirrors makePhotoRecord and the server's rekeyedFilename. */
function sanitisePromptKeyForFilename(key: string): string {
  return key.replace(/[^A-Za-z0-9_.-]+/g, '-').slice(0, 80);
}

/**
 * Drop a photo and any queued work for it. Used when the inspector removes a
 * photo a bulk import brought in by mistake.
 */
export async function deletePhotoLocally(photoId: string): Promise<void> {
  const db = await getDb();
  const tx = db.transaction(['photos', 'syncQueue'], 'readwrite');
  await Promise.all([
    tx.objectStore('photos').delete(photoId),
    // Leaving these would have the sync worker chase a photo that no longer
    // exists; it discards them, but the queue would carry dead weight forever.
    tx.objectStore('syncQueue').delete(`photo:${photoId}`),
    tx.objectStore('syncQueue').delete(`rekey:${photoId}`),
    tx.objectStore('syncQueue').delete(`rotate:${photoId}`),
  ]);
  await tx.done;
}

export async function getQueue() {
  const db = await getDb();
  return db.getAll('syncQueue');
}

export interface SyncOverview {
  pendingItems: number;
  failedItems: number;
  byJob: Record<string, 'draft' | 'pending' | 'sent' | 'failed'>;
}

export async function getSyncOverview(): Promise<SyncOverview> {
  const db = await getDb();
  // Only pull failed photos (via status index) — never getAll('photos'), which
  // would deserialize every captured blob on the home screen.
  const [queue, submissions, failedPhotos] = await Promise.all([
    db.getAll('syncQueue'),
    db.getAll('submissions'),
    readPhotos(db, 'by-status', 'failed'),
  ]);
  const byJob: SyncOverview['byJob'] = {};
  const queuedSubmissionJobs = new Set(
    queue.filter((item) => item.kind === 'submission').map((item) => item.jobId),
  );
  const queuedPhotoIds = new Set(
    queue.filter((item) => item.kind === 'photo').map((item) => item.photoId),
  );
  for (const submission of submissions) byJob[submission.jobId] = submission.status;
  let failedItems = submissions.filter(
    (item) => item.status === 'failed' && !queuedSubmissionJobs.has(item.jobId),
  ).length;
  for (const photo of failedPhotos) {
    if (!queuedPhotoIds.has(photo.id)) {
      failedItems += 1;
      byJob[photo.jobId] = 'failed';
    }
  }
  for (const item of queue) {
    if (byJob[item.jobId] !== 'failed') byJob[item.jobId] = 'pending';
  }
  return { pendingItems: queue.length, failedItems, byJob };
}

/** Ask the browser to exempt IndexedDB from eviction under storage pressure. */
export async function requestPersistentStorage(): Promise<boolean> {
  if (typeof navigator === 'undefined' || !navigator.storage?.persist) return false;
  try {
    if (await navigator.storage.persisted()) return true;
    return await navigator.storage.persist();
  } catch {
    return false;
  }
}

export async function dequeue(id: string) {
  const db = await getDb();
  await db.delete('syncQueue', id);
}

export async function bumpAttempt(id: string) {
  const db = await getDb();
  const row = await db.get('syncQueue', id);
  if (!row) return;
  await db.put('syncQueue', { ...row, attempts: row.attempts + 1 });
}

/* ────────────────────────────────────────────────────────────────────────────
 * Clause library cache
 * ──────────────────────────────────────────────────────────────────────────── */

export async function cacheClauseLibrary(payload: unknown) {
  const db = await getDb();
  await db.put('clauseLibrary', {
    id: 'current',
    cachedAt: new Date().toISOString(),
    payload,
  });
}

export async function getCachedClauseLibrary() {
  const db = await getDb();
  return db.get('clauseLibrary', 'current');
}
